import { getEnv } from '@/composables/helpers/getEnv'
import { fetchOneEntry, getBuilderSearchParams, isPreviewing } from '@builder.io/sdk-vue'

export default defineNuxtPlugin((nuxtApp) => {
    const { $config } = nuxtApp

    class BuilderAPI {
        constructor() {
            this.apiKey = getEnv('builderApi')
            this.apiUrl = getEnv('builderApiUrl') || 'https://cdn.builder.io/api/v3'
            this.builderEnv = getEnv('builderEnv')
            this.dataEnv = getEnv('dataEnv')
            this.productionCache = getEnv('productionCache') || 900
        }

        /**
         * Get a single entry by model and query parameters
         * @param {string} model - The Builder.io model name (e.g., 'page', 'blog', 'help')
         * @param {Object} options - Query options
         * @returns {Promise<Object>} - The content entry
         */
        async get(model, options = {}, key) {
            const route = useRoute()
            const now = new Date().getTime()
            
            // Merge default params with options
            const params = {
                model,
                apiKey: this.apiKey,
                options: { 
                    enrich: false,
                    ...getBuilderSearchParams(route.query),
                    ...options.options 
                },
                ...options
            }

            // Add environment query if not previewing
            if (!isPreviewing(route.fullPath) && !options.skipEnvironment) {
                const env = model === 'blog' ? this.dataEnv : this.builderEnv
                params.query = {
                    ...params.query,
                    data: {
                        ...params.query?.data,
                        environment: { $eq: env }
                    }
                }
            }

            try {
                return await useAsyncData(`${key}`, () => fetchOneEntry(params))
            } catch (error) {
                console.error(`Error fetching ${model}:`, error)
                return null
            }
        }

        /**
         * Get multiple entries by model and query parameters
         * @param {string} model - The Builder.io model name
         * @param {Object} options - Query options including limit, offset, query filters
         * @returns {Promise<Array>} - Array of content entries
         */
        async getAll(model, options = {}) {
            const { 
                limit = 100, 
                offset = 0, 
                query = {}, 
                fields,
                sort,
                includeUnpublished = false,
                cacheSeconds
            } = options

            const env = model === 'blog' ? this.dataEnv : this.builderEnv
            const cache = cacheSeconds !== undefined ? cacheSeconds : this.productionCache

            const params = {
                apiKey: this.apiKey,
                limit,
                offset,
                includeUnpublished,
                cacheSeconds: cache,
                'query.data.environment': env,
                ...query
            }

            if (fields) {
                params.fields = Array.isArray(fields) ? fields.join(',') : fields
            }

            if (sort) {
                Object.entries(sort).forEach(([key, value]) => {
                    params[`sort.${key}`] = value
                })
            }

            try {
                const response = await $fetch(`${this.apiUrl}/content/${model}`, {
                    method: 'GET',
                    params
                })
                return response.results || []
            } catch (error) {
                console.error(`Error fetching all ${model}:`, error)
                return []
            }
        }

        /**
         * Get content by ID
         * @param {string} model - The Builder.io model name
         * @param {string} id - The content ID
         * @returns {Promise<Object>} - The content entry
         */
        async getById(model, id) {
            const env = model === 'blog' ? this.dataEnv : this.builderEnv
            
            try {
                const response = await $fetch(`${this.apiUrl}/content/${model}/${id}`, {
                    method: 'GET',
                    params: {
                        apiKey: this.apiKey,
                        'query.data.environment': env,
                        cacheSeconds: this.productionCache
                    }
                })
                return response
            } catch (error) {
                console.error(`Error fetching ${model} by ID:`, error)
                return null
            }
        }

        /**
         * Search content by query parameters
         * @param {string} model - The Builder.io model name
         * @param {Object} searchParams - Search parameters
         * @returns {Promise<Array>} - Array of matching content entries
         */
        async search(model, searchParams = {}) {
            const { 
                text, 
                filters = {}, 
                limit = 20,
                offset = 0,
                fields
            } = searchParams

            const query = { ...filters }
            
            // Add text search if provided
            if (text) {
                query['$text'] = { $search: text }
            }

            return this.getAll(model, { 
                limit, 
                offset, 
                query, 
                fields,
                includeUnpublished: false 
            })
        }

        /**
         * Get content by specific field value
         * @param {string} model - The Builder.io model name
         * @param {string} field - The field to search by (e.g., 'name', 'data.slug')
         * @param {*} value - The value to match
         * @returns {Promise<Object>} - The first matching content entry
         */
        async getByField(model, field, value) {
            const results = await this.getAll(model, {
                limit: 1,
                query: {
                    [`query.${field}`]: value
                }
            })
            return results[0] || null
        }

        /**
         * Get related content by IDs
         * @param {string} model - The Builder.io model name
         * @param {Array<string>} ids - Array of content IDs
         * @param {Object} options - Additional query options
         * @returns {Promise<Array>} - Array of content entries
         */
        async getByIds(model, ids, options = {}) {
            if (!ids || ids.length === 0) return []

            return this.getAll(model, {
                ...options,
                query: {
                    ...options.query,
                    'query.id.$in': ids
                }
            })
        }

        /**
         * Get content for a specific URL path
         * @param {string} urlPath - The URL path to match
         * @param {string} model - The model to search (default: 'page')
         * @returns {Promise<Object>} - The matching content entry
         */
        async getByUrl(urlPath, model = 'page') {
            return this.get(model, {
                userAttributes: { urlPath },
                query: {
                    'data.url': urlPath
                }
            })
        }

        /**
         * Get global data
         * @returns {Promise<Object>} - Global data content
         */
        async getGlobalData() {
            const results = await this.getAll('global-data', { limit: 1 })
            return results[0] || null
        }

        /**
         * Get menu by name
         * @param {string} name - The menu name
         * @returns {Promise<Object>} - Menu content
         */
        async getMenu(name) {
            return this.getByField('menus', 'data.name', name)
        }

        /**
         * Check if content exists for a URL
         * @param {string} urlPath - The URL path to check
         * @param {string} model - The model to check (default: 'page')
         * @returns {Promise<boolean>} - True if content exists
         */
        async exists(urlPath, model = 'page') {
            const content = await this.getByUrl(urlPath, model)
            return !!content
        }

        /**
         * Get preview URL for content
         * @param {string} model - The Builder.io model name
         * @param {string} id - The content ID
         * @returns {string} - Preview URL
         */
        getPreviewUrl(model, id) {
            return `https://builder.io/content/${id}?model=${model}&apiKey=${this.apiKey}`
        }
    }

    // Create and inject the Builder API instance
    const builder = new BuilderAPI()
    
    return {
        provide: {
            builder
        }
    }
})